import { useState, Fragment } from 'react';
import IndexAnalysisChart from '@/components/analyticsPage/indexAnalysis';
import LoadingCheck from '@/components/analyticsPage/loadingCheck/LoadingCheck';
import IndexDrawer from '@/components/analyticsPage/tabPanels/components/drawers/indexDrawer/IndexDrawer';
import { useDownload } from '@/components/analyticsPage/tabPanels/hooks/useDownload';
import {
  convertIndexUritoQueryObj,
  useIndexAnalysis,
} from '@/components/analyticsPage/tabPanels/components/indexAnalysis/hooks/useIndexAnalysis';
import { useSelector } from 'react-redux';
import { useResetIndexDrawer } from '@/components/analyticsPage/tabPanels/components/indexAnalysis/hooks/useResetDrawer';
import ModalComponent from '@/ui//modals/defaultModal/Modal';
import ComponentPreloader from '@/ui//preloaders/componentPreloader/ComponentPreloader';
import { getUserData } from '@/api/user';
import { AnalyticsTab } from '@/types/analytics';
import { TooltipContent, TooltipTrigger, Tooltip } from '@/components/ui/tooltip';
import { AlertTriangle, Download, InfoIcon, Loader2, Settings, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { deleteAnalytics } from '@/api/analytics';
import { useNavigate } from 'react-router-dom';
import { useIndexQuery } from '../drawers/indexDrawer/hooks/useIndexQuery';

interface IndexAnalysisProps {
  tab: AnalyticsTab;
}

export const IndexAnalysis = ({ tab }: IndexAnalysisProps) => {
  const [column, rows, series, isNoResponse, loading] = useIndexAnalysis(tab);
  const [openIndexDrawer, setOpenIndexDrawer] = useState(false);
  const indexQuery = useSelector((state: any) => state.analytics.indexQuery);
  const user = useSelector((state: any) => state.user.info);
  const [download] = useDownload(column, rows, indexQuery.companyName_en);
  const [query] = useIndexQuery();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [t] = useTranslation('analytics');
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);

  const handleDownloadClick = async () => {
    try {
      const userCheck = await getUserData(user.id);
      if (userCheck?.data?.user_type === 'enterprise') {
        download();
      } else {
        setOpenModal(true);
      }
    } catch (e) {
      window.alert('Failed download.');
    }
  };

  const iframLoaded = () => {
    setIframeLoading(false);
  };

  const handleModalClose = () => {
    setIframeLoading(true);
    setOpenModal(false);
  };

  const handleDeleteClick = () => {
    setOpenDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    await deleteAnalytics({
      tabId: tab.id,
      userId: user.id,
    });
    setDeleteLoading(false);
    setOpenDeleteModal(false);
    setOpenIndexDrawer(false);
    navigate('/analytics');
  };

  const handleDeleteCancel = () => {
    setOpenDeleteModal(false);
  };

  const handleDrawerOpen = async (open: boolean) => {
    if (tab.corp_code) {
      const newQuery = await convertIndexUritoQueryObj(tab, indexQuery);
      query(newQuery);
    }
    setOpenIndexDrawer(open);
  };

  return (
    <>
      <div className="bg-muted rounded-lg p-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon className="w-4 h-4" />
              </TooltipTrigger>
              <TooltipContent>
                {t(
                  'Index Analytics provide an in-depth analysis function that can analyze companies by ESG topics, and provide long-term and short-term ESG risk indicators.',
                )}
              </TooltipContent>
            </Tooltip>
            <div className="text-2xl font-bold">
              Index Analysis <span className="text-sm font-normal"> - {tab.corp_name_en}</span>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 h-full">
              <Button onClick={() => handleDrawerOpen(true)}>
                <Settings />
                Settings
              </Button>

              <Button
                variant="destructive"
                className="text-white"
                size="icon"
                onClick={handleDeleteClick}
              >
                <Trash2 />
              </Button>
              <Button variant="outline" className="hidden md:flex" onClick={handleDownloadClick}>
                <Download />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Chart UI */}
        <div className="overflow-x-auto rounded-lg">
          <LoadingCheck isLoading={loading} isNoResponse={isNoResponse}>
            <IndexAnalysisChart data={series} />
          </LoadingCheck>
        </div>

        {/* Drawer UI */}
        <IndexDrawer
          open={openIndexDrawer}
          data={indexQuery}
          tab={tab}
          action="update"
          closeDrawer={() => setOpenIndexDrawer(false)}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <Transition appear show={openDeleteModal} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={handleDeleteCancel}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="h-6 w-6 text-destructive" />
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{t('Delete data')}</h3>
                      <p className="text-sm text-gray-500">
                        {t('Are you sure you want to delete this data?')}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <Button variant="outline" onClick={handleDeleteCancel}>
                      {t('Cancel')}
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={deleteLoading}
                      className="text-white"
                      onClick={handleDeleteConfirm}
                    >
                      {deleteLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {t('Delete')}
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={openModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleModalClose}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-[690px] transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div style={{ position: 'relative' }}>
                    {iframeLoading && (
                      <div
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          translate: '-50% -50%',
                        }}
                      >
                        <ComponentPreloader />
                      </div>
                    )}
                    <div style={{ height: '30px' }} />
                    <iframe
                      src="https://docs.google.com/forms/d/e/1FAIpQLSepD-PA2voKvTytaM1cfrsCjVVNCB6aXq7md1Pp_E0usIEx8A/viewform?embedded=true"
                      width={640}
                      height={window.innerHeight * 0.7}
                      onLoad={iframLoaded}
                    />
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default IndexAnalysis;
